import { useEffect, useState } from "react";
import { api, act, useFetch, fmtDate } from "@/lib/api";
import { PageHeader, Panel, Badge, Md, Field, Empty } from "@/components/common";
import { Sel } from "@/components/Sel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";

const NUM = ["research_pages_per_run", "monthly_budget_usd", "ai_budget_usd", "daily_send_limit", "contact_frequency_days", "max_active_campaigns", "target_meetings", "expiry_days"];
const TEXT = [["objective", "Objective"], ["qualification_criteria", "Qualification criteria"], ["approved_claims_policy", "Claims policy"],
  ["brand_standards", "Brand standards"], ["inferred_consent_basis", "Inferred-consent basis (documented)"], ["pricing_authority", "Pricing authority"], ["escalation_rules", "Escalation rules & owners"]];

export default function Charter() {
  const { data, reload } = useFetch("/charter");
  const { data: products } = useFetch("/products");
  const { data: segments } = useFetch("/segments");
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { if (data?.current) setF(data.current); }, [data]);
  if (!f) return <div className="text-zinc-500 font-mono text-sm caret">Loading charter</div>;
  const set = (k, v) => setF({ ...f, [k]: v });
  const toggle = (k, id) => set(k, (f[k] || []).includes(id) ? f[k].filter((x) => x !== id) : [...(f[k] || []), id]);
  const cur = data.current;
  const dirty = JSON.stringify(f) !== JSON.stringify(cur);

  const save = async () => { setBusy("save"); await act(() => api.put("/charter", f), "Saved as new draft version"); await reload(); setBusy(""); };
  const summary = async () => { setBusy("sum"); await act(() => api.post("/charter/summary"), "Plain-language summary ready"); await reload(); setBusy(""); };
  const approve = async () => { setBusy("ok"); await act(() => api.post("/charter/approve"), "Charter approved — agents may act within it"); await reload(); setBusy(""); };

  return (
    <div data-testid="charter-page">
      <PageHeader eyebrow="AIO01 · Operating charter" title="Boundaries, once." desc="Approve the charter once; agents launch and execute qualifying work without per-action approval. Anything outside becomes an exception.">
        <Button variant="secondary" data-testid="charter-save-btn" disabled={!dirty || busy} onClick={save}>{busy === "save" ? "Saving…" : "Save new version"}</Button>
        <Button variant="secondary" data-testid="charter-summary-btn" disabled={dirty || !!busy} onClick={summary}>{busy === "sum" ? "Summarising…" : "AI plain-language summary"}</Button>
        <Button data-testid="charter-approve-btn" disabled={dirty || cur.status === "approved" || !!busy} onClick={approve} className="bg-[#00C781] hover:bg-[#00C781]/80 text-black">Approve v{cur.version}</Button>
      </PageHeader>
      <div className="flex items-center gap-3 mb-5 text-sm" data-testid="charter-status">
        <Badge v={cur.status} /> <span className="font-mono text-zinc-400">v{cur.version}</span>
        {cur.approved_by && <span className="text-zinc-500">approved by {cur.approved_by} · expires {fmtDate(cur.expires_at)}</span>}
        {dirty && <Badge v="unsaved changes" tone="amber" />}
      </div>
      <div className="grid lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 space-y-3">
          <Panel title="Scope">
            <div className="grid md:grid-cols-2 gap-6">
              <div><div className="text-xs text-zinc-400 mb-2">Products in scope</div>
                {(products || []).map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm py-1"><Checkbox data-testid={`charter-product-${p.id}`} checked={(f.product_ids || []).includes(p.id)} onCheckedChange={() => toggle("product_ids", p.id)} />{p.name} <Badge v={p.status} /></label>
                ))}</div>
              <div><div className="text-xs text-zinc-400 mb-2">Allowed segments</div>
                {(segments || []).map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm py-1"><Checkbox data-testid={`charter-segment-${s.id}`} checked={(f.segment_ids || []).includes(s.id)} onCheckedChange={() => toggle("segment_ids", s.id)} />{s.name}</label>
                ))}</div>
            </div>
          </Panel>
          <Panel title="Rules & authority">
            <div className="space-y-4">{TEXT.map(([k, l]) => (
              <Field key={k} label={l}><Textarea data-testid={`charter-${k}`} rows={k === "objective" ? 3 : 2} value={f[k] || ""} onChange={(e) => set(k, e.target.value)} className="bg-black" /></Field>
            ))}</div>
          </Panel>
        </div>
        <div className="space-y-3">
          <Panel title="Autonomy">
            <Field label="Mode" hint="Draft: agents only draft. Supervised: launches & replies need approval. Autonomous: bounded execution.">
              <Sel testid="charter-autonomy-select" value={f.autonomy_mode} onChange={(v) => set("autonomy_mode", v)} options={["draft", "supervised", "autonomous"]} />
            </Field>
            <label className="flex items-center justify-between mt-4 text-sm">Inferred consent for researched contacts<Switch data-testid="charter-inferred-consent" checked={!!f.inferred_consent_enabled} onCheckedChange={(v) => set("inferred_consent_enabled", v)} /></label>
            <label className="flex items-center justify-between mt-4 text-sm">AI voice calls in sequences<Switch data-testid="charter-voice-enabled" checked={!!f.voice_enabled} onCheckedChange={(v) => set("voice_enabled", v)} /></label>
            <label className="flex items-center justify-between mt-4 text-sm">Enforce local business hours (9–5)<Switch data-testid="charter-business-hours" checked={!!f.enforce_business_hours} onCheckedChange={(v) => set("enforce_business_hours", v)} /></label>
          </Panel>
          <Panel title="Hard limits">
            <div className="grid grid-cols-2 gap-3">{NUM.map((k) => (
              <Field key={k} label={k.replace(/_/g, " ").replace("usd", "(USD)")}><Input type="number" data-testid={`charter-${k}`} value={f[k] ?? ""} onChange={(e) => set(k, Number(e.target.value))} className="bg-black tnum" /></Field>
            ))}</div>
          </Panel>
          <Panel title="Plain-language summary">
            {cur.summary ? <div data-testid="charter-summary"><Md text={cur.summary} /></div> : <Empty>Generate a summary before approving.</Empty>}
          </Panel>
          <Panel title="Version history">
            <ul className="space-y-1.5 text-xs font-mono">{data.history.map((h) => (
              <li key={h.id} className="flex justify-between"><span>v{h.version} · {h.edited_by}</span><Badge v={h.status} /></li>
            ))}</ul>
          </Panel>
        </div>
      </div>
    </div>
  );
}
